import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import Drawer from "antd/es/drawer";
import Form from "antd/es/form";
import { useForm } from "antd/es/form/Form";
import Steps from "antd/es/steps";
import cn from "classnames";
import { useIntl } from "react-intl";

import FieldVisibility from "@mapmycustomers/shared/enum/FieldVisibility";
import NumberFormatType from "@mapmycustomers/shared/enum/NumberFormatType";
import CustomField, {
  CustomFieldPayload,
} from "@mapmycustomers/shared/types/customField/CustomField";
import { EntityTypeSupportingCustomFields } from "@mapmycustomers/shared/types/entity";
import Role from "@mapmycustomers/shared/types/Role";
import Team from "@mapmycustomers/shared/types/Team";

import { createField } from "@app/scene/settings/component/ManageFields/store/actions";
import CreateEditCustomFieldStep from "@app/scene/settings/enum/CreateEditCustomFieldStep";
import { getCustomFieldsForEntity } from "@app/store/customFields";
import { updateCustomField } from "@app/store/customFields/actions";
import { getRolesExcludingOwners, getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { AnalyticsService, useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import getDefaultDrawerContainer from "@app/util/ui/getPageContentContainer";
import useHiddenChat from "@app/util/useHiddenChat";

import { getCreateEditEntityType, getEditCustomFieldStep, getEditedCustomField } from "../../store";
import DefaultCustomFieldFormValues from "../../type/DefaultCustomFieldFormValues";

import BasicInfo from "./component/BasicInfo";
import FieldSetup from "./component/FieldSetup";
import ShareSave from "./component/ShareSave";
import styles from "./CreateEditPane.module.scss";
import useHandleFinish from "./util/useHandleFinish";
import useSteps from "./util/useSteps";

interface Props {
  analyticIssuer?: AnalyticsService;
  createCustomField: typeof createField.request;
  customField: CustomField | CustomFieldPayload;
  customFields: CustomField[];
  entityType: EntityTypeSupportingCustomFields;
  onHide: () => void;
  onSave?: () => void;
  roles: Role[];
  step: CreateEditCustomFieldStep;
  teams: Team[];
  updateCustomField: typeof updateCustomField.request;
}

const CreateEditPane: React.FC<Props> = ({
  analyticIssuer,
  createCustomField,
  customField,
  customFields,
  entityType,
  onHide,
  onSave,
  roles,
  step,
  teams,
  updateCustomField,
}) => {
  const intl = useIntl();
  const [form] = useForm<DefaultCustomFieldFormValues>();

  const createFieldAnalytics = useExtendedAnalytics("Add Custom Field", analyticIssuer);
  useEffect(() => {
    createFieldAnalytics?.clicked([]);
  }, [createFieldAnalytics]);

  const steps = useSteps(intl);

  const handleFinish = useHandleFinish(
    createCustomField,
    customField,
    customFields,
    entityType,
    intl,
    form,
    onSave ? onSave : onHide,
    updateCustomField,
    roles,
    teams,
    analyticIssuer
  );

  const handleClose = useCallback(() => {
    const addEditStep =
      step === CreateEditCustomFieldStep.BASIC_INFO
        ? "Basic Info"
        : step === CreateEditCustomFieldStep.FIELD_SETUP
        ? "Field Setup"
        : "Share & Save";
    analyticIssuer?.cancelled([addEditStep, "Close"]);
    onHide();
  }, [analyticIssuer, onHide, step]);

  useHiddenChat();

  return (
    <Drawer
      className={styles.container}
      closable
      getContainer={getDefaultDrawerContainer()}
      onClose={handleClose}
      placement="right"
      title={intl.formatMessage({
        id: "settings.customFields.createEditPane.title",
        defaultMessage: "New Custom Field",
        description: "Settings Custom field - title of create /edit",
      })}
      visible
    >
      <Steps className={styles.stepsBreadcrumb} current={step}>
        {steps.map((title, index) => (
          <Steps.Step disabled={step > index} icon={<></>} key={title} title={title} />
        ))}
      </Steps>

      <Form<DefaultCustomFieldFormValues>
        className={styles.stepsContainer}
        form={form}
        initialValues={{
          currencyId: customField.currencyId,
          dataType: customField.dataType,
          displayName: customField.displayName,
          displayType: customField.displayType ?? NumberFormatType.NUMERIC,
          isUnique: customField.isUnique ?? false,
          options: customField.options,
          phiEnabled: false,
          placeholder: customField?.placeholder ?? "",
          roleVisibility: {},
          teamId: customField?.teamId ?? [],
          teamVisibility: [],
          unifiedVisibility: true,
          visibility: FieldVisibility.FULL_ACCESS,
        }}
        layout="vertical"
        onFinish={handleFinish}
        preserve
      >
        <div
          className={cn(styles.stepContainer, {
            [styles.hiddenStep]: step !== CreateEditCustomFieldStep.BASIC_INFO,
          })}
        >
          <BasicInfo analyticIssuer={createFieldAnalytics} editing={!!customField.id} />
        </div>

        <div
          className={cn(styles.stepContainer, {
            [styles.hiddenStep]: step !== CreateEditCustomFieldStep.FIELD_SETUP,
          })}
        >
          <FieldSetup analyticIssuer={createFieldAnalytics} form={form} />
        </div>

        <div
          className={cn(styles.stepContainer, {
            [styles.hiddenStep]: step !== CreateEditCustomFieldStep.SHARE_SAVE,
          })}
        >
          <ShareSave analyticIssuer={createFieldAnalytics} form={form} />
        </div>
      </Form>
    </Drawer>
  );
};

const mapStateToProps = (state: RootState) => ({
  customField: getEditedCustomField(state),
  customFields: getCustomFieldsForEntity(state)(getCreateEditEntityType(state)),
  entityType: getCreateEditEntityType(state),
  roles: getRolesExcludingOwners(state),
  step: getEditCustomFieldStep(state),
  teams: getTeams(state),
});

const mapDispatchToProps = {
  createCustomField: createField.request,
  updateCustomField: updateCustomField.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateEditPane);
